// The held sign-in's page (open-link-confirm.js): every refusal auth answers
// on its link-confirmation steps, and every outcome a method proof leaves on
// the hold (`last.result`), as what the person reads. Written from the
// person's side, sentence case, specific about what to do next; no
// apologies. "another" says whether the page still offers another way to
// confirm, so a message never points at a way that isn't there.

// auth's refusal reasons (rapid-api-auth link-confirm.go, link-confirm-browser.go)
export const REASON = {
  failed: 'link_confirmation_failed', // 400: wrong password (or none), wrong/expired/used code
  locked: 'link_confirmation_locked', // 429: the account's sign-in lockout; message is its sentence
  rateLimited: 'link_confirmation_rate_limited', // 429: this sign-in's tries, or the address's/account's
  unavailable: 'link_confirmation_unavailable', // 403: the domain requires single sign-on
  notRequired: 'link_confirmation_not_required', // 409: not held (confirmed meanwhile)
  changed: 'link_confirmation_changed', // 409: kept changing under concurrent requests
  otherBrowser: 'link_confirmation_other_browser', // 403: not the browser that holds the sign-in
  codeRateLimited: 'link_code_rate_limited', // 429 + resendAfter (null: no more sends)
  codeUnavailable: 'link_code_unavailable' // 503: the code can't be emailed (hide the option)
}

export const reasonOf = error => (error && error.data && typeof error.data.reason === 'string' ? error.data.reason : null)

// The server's own sentence, when it sent one.
export const serverSentence = error => {
  const message = error && error.data && error.data.message
  return typeof message === 'string' && message.trim() ? message.trim() : null
}

const orAnother = another => (another ? ', or use another way' : '')

// auth's sentence for this sign-in's own 5 tries (MessageLinkTooManyTries):
// final for the sign-in. Its other link_confirmation_rate_limited sentence
// ("Too many tries. Wait a while...": the address's or the account's
// budget) passes, so only this one locks the field (review L3). Should auth
// reword it, the page only stops locking: the next try gets the same 429.
export const TOO_MANY_TRIES_FOR_SIGN_IN = 'Too many tries for this sign-in. Start again from the sign-in page.'

export const triesSpentForSignIn = error => serverSentence(error) === TOO_MANY_TRIES_FOR_SIGN_IN

// A held sign-in lasts at most 15 minutes from its hold: a code send limit
// that lifts later than that (an account's hourly or daily budget) lifts
// too late for this sign-in, which is the same as no more sends.
const SIGN_IN_LIFE_SECONDS = 15 * 60

// When another code may be sent, from auth's resendAfter: undefined (now,
// or unknown), an ISO time, or null (never, on this sign-in).
export const nextSendFrom = resendAfter => {
  if (resendAfter === undefined) return undefined
  if (resendAfter === null || secondsUntil(resendAfter) > SIGN_IN_LIFE_SECONDS) return null
  return resendAfter
}

const START_AGAIN = ' Start again from the sign-in page.'

export const COPY = {
  wrongPassword: another => `That password isn't right. Try again${orAnother(another)}.`,
  locked: (sentence, another) => `${sentence || 'Too many wrong passwords for this account. Try again later.'}${another ? ' Or use another way.' : ''}`,
  tooManyTries: sentence => sentence || TOO_MANY_TRIES_FOR_SIGN_IN,
  ssoOnly: another => `Your organization signs in with single sign-on, so a password can't confirm this account.${another ? ' Use another way.' : ''}`,
  changed: 'This sign-in changed while we were checking it. Try again.',
  passwordFault: "We couldn't check your password just now. Try again.",

  codeShape: 'Enter the 8-digit code from the email.',
  wrongCode: (canResend, another) =>
    `That code isn't right or has expired. Check the latest email${canResend ? ', or send a new code' : orAnother(another)}.`,
  // the code on the page is past its expiresAt: next is 'send', 'another' or 'restart'
  codeExpiredLead: 'That code has expired.',
  codeExpired: next => `That code has expired.${next === 'send' ? ' Send a new code.' : next === 'another' ? ' Use another way.' : START_AGAIN}`,
  codeFault: "We couldn't check the code just now. Try again.",
  sendLimit: another => (another ? "We've sent a few codes already. Wait a while, or use another way." : "We've sent a few codes already. Wait a while, then send a new one."),
  noMoreSends: another => `No more codes can be sent for this sign-in. ${another ? 'Use another way, or start again' : 'Start again'} from the sign-in page.`,
  noMoreSendsCode: 'No more codes can be sent for this sign-in. Use the one in your latest email.',
  emailUnavailable: another => (another ? "We can't email a code right now. Use another way." : "We can't email a code right now."),
  resendUnavailable: another => `We can't send a new code right now. Use the code we sent before${orAnother(another)}.`,
  sendFault: "We couldn't send a code just now. Try again.",
  resent: 'We sent a new code. Use the one in the latest email.',

  // "We can't confirm this account here": nothing to confirm with from the
  // start (the plan's words); nothing LEFT on this sign-in (its codes, its
  // method starts, an expired code); the domain requires single sign-on.
  noWay: 'This account has no password or other sign-in method we can check, and we can\'t email a code right now. Sign in the way you usually do, or contact support.',
  noWayLeft: "There's no way left to confirm this account on this sign-in. Start again from the sign-in page.",
  noWaySso: "Your organization signs in with single sign-on, so a password can't confirm this account, and there's no other way to confirm it here. Sign in with your organization's single sign-on, or contact support."
}

// A method proof's outcome, as the page says it on arrival (auth records it
// on the hold: the proof comes back through a redirect, and a refused start
// goes back to this page). last.provider is the method, when still a
// connection; offered: it is still a button on this page; another: the page
// offers another way. Results (auth eadee14):
//
//   mismatch      the proof signed in as another account or identity
//   browser       it finished in another browser, or its ID token carried
//                 another sign-in's nonce
//   not_linked    the method no longer confirms the account
//   rate_limited  a refused start: naming the method, the address's budget
//                 (wait, then again); naming none, this sign-in's 5 starts
//   unavailable   a refused start naming the method: no way to tie it to
//                 this browser here, or its identity provider refused it
//
// Anything else ("stale" is no longer sent, "failed") reads as a plain
// "didn't confirm". Nothing says "try again" when the method is no longer
// a button here (review L1): then another way, or start again.
//
// { tone, base (what happened), text (base and what to do), restart }
export const lastNotice = (last, { offered = false, another = false } = {}) => {
  if (!last || !last.result) return null

  const name = last.provider && last.provider.displayName
  const that = `That ${name ? `${name} ` : ''}sign-in`
  const elsewhere = another ? { text: ' Use another way.' } : { text: START_AGAIN, restart: true }
  const retry = sentence => (offered ? { text: ` ${sentence}${orAnother(another)}.` } : elsewhere)
  const make = (tone, base, next) => ({ tone, base, text: base + next.text, restart: Boolean(next.restart) })

  switch (last.result) {
    case 'mismatch':
      return make('bad', `${that} was for a different account, so it didn't confirm this one.`, retry('Try again with the right account'))
    case 'browser':
      return make('bad', `${that} didn't match the one started in this browser, so it didn't count.`, retry('Start it again here'))
    case 'not_linked':
      return make('bad', `${name || 'That sign-in method'} can no longer confirm this account.`, elsewhere)
    case 'rate_limited':
      return last.provider
        ? make('warn', 'Too many sign-ins were started from your network just now.', retry(`Wait a minute, then try ${name} again`))
        : make('warn', "You've started too many sign-ins for this confirmation.", another ? { text: ' Use another way, or start again from the sign-in page.', restart: true } : elsewhere)
    case 'unavailable':
      return offered
        ? make('warn', `We couldn't start a sign-in with ${name}.`, retry('Try again'))
        : make('warn', `${name || 'That sign-in method'} can't confirm this account here.`, elsewhere)
    default:
      return make('bad', "That sign-in didn't confirm this account.", retry('Try again'))
  }
}

// How long until something, in words: seconds up to a minute and a half,
// then whole minutes (rounded up).
export const waitText = seconds => {
  const s = Math.max(1, Math.ceil(seconds))
  if (s <= 90) return `${s} second${s === 1 ? '' : 's'}`
  const m = Math.ceil(s / 60)
  return `${m} minute${m === 1 ? '' : 's'}`
}

// Seconds from now until an ISO time; 0 when it has passed or isn't one.
export const secondsUntil = (iso, now = Date.now()) => {
  const at = typeof iso === 'string' ? Date.parse(iso) : NaN
  return Number.isNaN(at) ? 0 : Math.max(0, (at - now) / 1000)
}

// "It expires in 10 minutes." for a code's expiresAt.
export const expiresText = (iso, now = Date.now()) => {
  const minutes = Math.max(1, Math.ceil(secondsUntil(iso, now) / 60))
  return `It expires in ${minutes} minute${minutes === 1 ? '' : 's'}.`
}
